import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Eye, Filter, Check, X, RefreshCw, Calendar, Users, Building, Mail, Phone } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending_approval: { label: 'Pending Approval', color: 'bg-amber-500', icon: '🟡' },
  pending_scheduling: { label: 'Awaiting Customer', color: 'bg-orange-500', icon: '🟠' },
  scheduled: { label: 'Scheduled', color: 'bg-blue-500', icon: '🔵' },
  completed: { label: 'Completed', color: 'bg-green-500', icon: '🟢' },
  cancelled: { label: 'Cancelled', color: 'bg-red-500', icon: '🔴' },
  no_show: { label: 'No Show', color: 'bg-gray-500', icon: '⚪' },
  rejected: { label: 'Rejected', color: 'bg-red-700', icon: '⛔' }
};

const CUSTOMER_TYPE_LABELS = {
  new_client: 'New Client',
  existing_client: 'Existing Client',
  partner: 'Partner',
  other: 'Other'
};

export default function ConsultationList({ token, teamMembersList = [], currentUser = null }) {
  const navigate = useNavigate();
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    customer_type: '',
    search: ''
  });
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  
  // Approval/Rejection modals (keep these for approval workflow)
  const [selectedConsultation, setSelectedConsultation] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [approvalEventType, setApprovalEventType] = useState('introductory');
  const [rejectionReason, setRejectionReason] = useState('');
  
  // Bulk delete functionality
  const [selectedForDelete, setSelectedForDelete] = useState([]);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  
  const handleAssignConsultation = async (consultationId, assignedTo) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/admin/consultations/${consultationId}/assign`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ assigned_to: assignedTo || null })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'Consultation assigned successfully', {
          description: assignedTo ? 'Team member will now see this consultation' : 'Consultation unassigned'
        });
        fetchConsultations(); // Refresh list
      } else {
        const error = await response.json();
        toast.error('Failed to assign consultation', {
          description: error.detail || 'Please try again'
        });
      }
    } catch (error) {
      console.error('Error assigning consultation:', error);
      toast.error('Failed to assign consultation', {
        description: 'Network error'
      });
    }
  };
  
  useEffect(() => {
    fetchConsultations();
  }, [filters, pagination.page]);
  
  const fetchConsultations = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page,
        limit: 20,
        ...(filters.status && { status: filters.status }),
        ...(filters.customer_type && { customer_type: filters.customer_type }),
        ...(filters.search && { search: filters.search })
      });
      
      const response = await fetch(`${BACKEND_URL}/api/v1/admin/consultations?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setConsultations(data.consultations);
        setPagination({
          page: data.page,
          pages: data.pages,
          total: data.total
        });
      } else {
        toast.error('Failed to fetch consultations');
      }
    } catch (error) {
      console.error('Error fetching consultations:', error);
      toast.error('Error loading consultations');
    } finally {
      setLoading(false);
    }
  };
  
  const handleViewDetails = (consultation) => {
    // Navigate to dedicated consultation detail page with state passing
    navigate(`/leadboard/consultations/${consultation.consultation_id}`, {
      state: { consultation }
    });
  };
  
  const handleApprove = async () => {
    if (!selectedConsultation) return;
    
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/v1/admin/consultations/${selectedConsultation.consultation_id}/approve`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.ok) {
        toast.success('Consultation approved. Customer will receive email to schedule their time slot.');
        setShowApprovalModal(false);
        fetchConsultations();
      } else {
        toast.error('Failed to approve consultation');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error approving consultation');
    }
  };
  
  const handleReject = async () => {
    if (!selectedConsultation) return;
    
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/v1/admin/consultations/${selectedConsultation.consultation_id}/reject?reason=${encodeURIComponent(rejectionReason)}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.ok) {
        toast.success('Consultation rejected');
        setShowRejectionModal(false);
        setRejectionReason('');
        fetchConsultations();
      } else {
        toast.error('Failed to reject consultation');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error rejecting consultation');
    }
  };
  
  const handleBulkDelete = async () => {
    if (selectedForDelete.length === 0) return;
    
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/v1/admin/consultations/bulk-delete`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(selectedForDelete)
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Successfully deleted ${data.deleted_count} consultation(s)`);
        setShowBulkDeleteConfirm(false);
        setSelectedForDelete([]);
        fetchConsultations();
      } else {
        const error = await response.json();
        toast.error('Failed to delete consultations', {
          description: error.detail || 'Please try again'
        });
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error deleting consultations', {
        description: 'Network error'
      });
    }
  };
  
  const toggleSelectForDelete = (consultationId) => {
    setSelectedForDelete(prev => 
      prev.includes(consultationId)
        ? (prev || []).filter(id => id !== consultationId)
        : [...prev, consultationId]
    );
  };
  
  const toggleSelectAll = () => {
    if (selectedForDelete.length === consultations.length) {
      setSelectedForDelete([]);
    } else {
      setSelectedForDelete(consultations.map(c => c.consultation_id));
    }
  };

  
  return (
    <div>
      {/* Filters */}
      <Card className="bg-ivory mb-6">
        <CardHeader>
          <CardTitle className="text-navy flex items-center">
            <Filter className="w-5 h-5 mr-2" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium text-navy mb-2 block">Status</label>
              <Select data-testid="consultation-list-select-2" value={filters.status || "all"} onValueChange={(value) => setFilters({ ...filters, status: value === "all" ? "" : value })}>
                <SelectTrigger data-testid="consultation-list-selecttrigger-1">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem data-testid="consultation-list-selectitem-6" value="all">All Statuses</SelectItem>
                  <SelectItem data-testid="consultation-list-selectitem-5" value="pending_approval">Pending Approval</SelectItem>
                  <SelectItem data-testid="consultation-list-selectitem-4" value="scheduled">Scheduled</SelectItem>
                  <SelectItem data-testid="consultation-list-selectitem-3" value="completed">Completed</SelectItem>
                  <SelectItem data-testid="consultation-list-selectitem-2" value="cancelled">Cancelled</SelectItem>
                  <SelectItem data-testid="consultation-list-selectitem-1" value="no_show">No Show</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium text-navy mb-2 block">Customer Type</label>
              <Select data-testid="consultation-list-select-1" value={filters.customer_type || "all"} onValueChange={(value) => setFilters({ ...filters, customer_type: value === "all" ? "" : value })}>
                <SelectTrigger data-testid="consultation-list-selecttrigger-2">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem data-testid="consultation-list-selectitem-7" value="all">All Types</SelectItem>
                  <SelectItem data-testid="consultation-list-selectitem-6" value="new_client">New Client</SelectItem>
                  <SelectItem data-testid="consultation-list-selectitem-5" value="existing_client">Existing Client</SelectItem>
                  <SelectItem data-testid="consultation-list-selectitem-4" value="partner">Partner</SelectItem>
                  <SelectItem data-testid="consultation-list-selectitem-3" value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium text-navy mb-2 block">Search</label>
              <Input data-testid="consultation-list-input-1"
                placeholder="Name, email, company, ID..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="bg-white"
              />
            </div>
          </div>
          
          <div className="mt-4 flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Showing {consultations.length} of {pagination.total} consultations
            </div>
            <div className="flex gap-2">
              {selectedForDelete.length > 0 && (
                <Button
                  onClick={() => setShowBulkDeleteConfirm(true)}
                  variant="destructive"
                  size="sm"
                  data-testid="bulk-delete-button"
                >
                  <X className="w-4 h-4 mr-2" />
                  Delete Selected ({selectedForDelete.length})
                </Button>
              )}
              <Button data-testid="consultation-list-button-9"
                onClick={fetchConsultations}
                variant="outline"
                size="sm"
                className="border-navy text-navy hover:bg-navy hover:text-ivory"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Consultations Table */}
      <Card className="bg-ivory">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading consultations...</div>
          ) : consultations.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No consultations found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-navy text-ivory">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedForDelete.length === consultations.length && consultations.length > 0}
                        onChange={toggleSelectAll}
                        className="w-4 h-4"
                        data-testid="select-all-checkbox"
                      />
                    </th>
                    <th className="px-4 py-3 text-left">ID</th>
                    <th className="px-4 py-3 text-left">Customer</th>
                    <th className="px-4 py-3 text-left">Type</th>
                    <th className="px-4 py-3 text-left">Status</th>
                    {currentUser?.role === 'admin' && (
                      <th className="px-4 py-3 text-left">Assigned To</th>
                    )}
                    <th className="px-4 py-3 text-left">Created</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {consultations.map((consultation) => (
                    <tr data-testid="consultation-list-tr-1"
                      key={consultation.consultation_id}
                      className="border-b border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => handleViewDetails(consultation)}
                    >
                      <td data-testid="consultation-list-td-2" className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedForDelete.includes(consultation.consultation_id)}
                          onChange={() => toggleSelectForDelete(consultation.consultation_id)}
                          className="w-4 h-4"
                          data-testid={`checkbox-${consultation.consultation_id}`}
                        />
                      </td>
                      <td className="px-4 py-3 text-sm font-mono">{consultation.consultation_id.slice(-8)}</td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-navy">{consultation.customer_info?.name}</div>
                        <div className="text-xs text-gray-500">{consultation.customer_info?.email}</div>
                      </td>
                      <td className="px-4 py-3 text-sm">{CUSTOMER_TYPE_LABELS[consultation.customer_type]}</td>
                      <td className="px-4 py-3">
                        <Badge className={`${STATUS_CONFIG[consultation.status]?.color} text-white`}>
                          {STATUS_CONFIG[consultation.status]?.icon} {STATUS_CONFIG[consultation.status]?.label}
                        </Badge>
                      </td>
                      {currentUser?.role === 'admin' && (
                        <td data-testid="consultation-list-td-1" className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <Select data-testid="consultation-list-select-1"
                            value={consultation.assigned_to || 'unassigned'}
                            onValueChange={(value) => handleAssignConsultation(consultation._id, value === 'unassigned' ? null : value)}
                          >
                            <SelectTrigger data-testid="consultation-list-selecttrigger-1" className="w-[180px]">
                              <SelectValue placeholder="Unassigned" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem data-testid="consultation-list-selectitem-2" value="unassigned">Unassigned</SelectItem>
                              {teamMembersList.filter(m => m.role === 'member').map(member => (
                                <SelectItem data-testid="consultation-list-selectitem-1" key={member.member_id} value={member.member_id}>
                                  {member.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                      )}
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {new Date(consultation.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button data-testid="consultation-list-button-8"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewDetails(consultation);
                          }}
                          size="sm"
                          variant="outline"
                          className="border-navy text-navy hover:bg-navy hover:text-ivory"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="flex justify-center items-center gap-2 p-4 border-t">
              <Button data-testid="consultation-list-button-7"
                onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                disabled={pagination.page === 1}
                size="sm"
                variant="outline"
              >
                Previous
              </Button>
              <span className="text-sm text-gray-600">
                Page {pagination.page} of {pagination.pages}
              </span>
              <Button data-testid="consultation-list-button-6"
                onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                disabled={pagination.page === pagination.pages}
                size="sm"
                variant="outline"
              >
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
      
      
      {/* Approval Modal */}
      <Dialog open={showApprovalModal} onOpenChange={setShowApprovalModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Consultation</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-[#F7F6F2] p-4 rounded-lg">
              <p className="text-sm text-[#4C5A70]">
                Approving this consultation will send an email to the customer allowing them to select their preferred time slot.
              </p>
            </div>
            
            <div className="flex gap-2 justify-end">
              <Button data-testid="consultation-list-button-5" variant="outline" onClick={() => setShowApprovalModal(false)}>Cancel</Button>
              <Button data-testid="consultation-list-button-4"
                onClick={handleApprove}
                className="bg-gradient-to-r from-[#E8D89E] via-[#D4AF37] to-[#B99610] text-black font-semibold"
              >
                Confirm Approval
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Rejection Modal */}
      <Dialog open={showRejectionModal} onOpenChange={setShowRejectionModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Consultation</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Reason for Rejection</label>
              <Textarea data-testid="consultation-list-textarea-1"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Enter reason for rejection..."
                rows={4}
              />
            </div>
            
            <div className="flex gap-2 justify-end">
              <Button data-testid="consultation-list-button-3" variant="outline" onClick={() => setShowRejectionModal(false)}>Cancel</Button>
              <Button data-testid="consultation-list-button-2"
                onClick={handleReject}
                className="bg-red-500 hover:bg-red-600 text-white"
              >
                Confirm Rejection
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Modal */}
      <Dialog open={showBulkDeleteConfirm} onOpenChange={setShowBulkDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Bulk Delete</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-gray-700">
              <p className="mb-2">Are you sure you want to delete <strong>{selectedForDelete.length}</strong> consultation(s)?</p>
              <p className="text-sm text-red-600">This action cannot be undone.</p>
            </div>
            
            <div className="flex gap-2 justify-end">
              <Button data-testid="consultation-list-button-1" variant="outline" onClick={() => setShowBulkDeleteConfirm(false)}>Cancel</Button>
              <Button
                onClick={handleBulkDelete}
                className="bg-red-500 hover:bg-red-600 text-white"
                data-testid="confirm-bulk-delete"
              >
                Delete {selectedForDelete.length} Consultation(s)
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}
